<template>
    <VNumericPanel :numeric="numeric" :task="task" @close="close" v-if="numeric"></VNumericPanel>
</template>

<script setup lang="ts">
// Components
import VNumericPanel from '../components/VNumericPanel.vue';

// Other
import { pushPageTitle, pullPageTitle } from '@/utils/document-utils';
import emitter from '@/core/Emitter';
import { EventNames } from '@/core/EventNames';
import { onMounted, watch, defineEmits } from 'vue';
import { $error } from '@/utils/app-utils';

import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { computed, ref } from '@vue/reactivity';
import { onBeforeUnmount } from 'vue';
import NumericsService from '@/core/Services/NumericService';
import INumeric from '@/core/Models/INumeric';
import TaskService from '@/core/Services/TaskService';
import ITask from '@/core/Models/ITask';
import { useNumericMutator } from '@/mixins/NumericApi';
import scrollIntoView from 'scroll-into-view';

const emit = defineEmits(['configure', 'loading', 'no-data', 'close', 'before-unmount']);
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { trackMutations } = useNumericMutator({
    mapCounters: true,
});

const numericId = computed(() => parseInt(route.query.numeric as string) || 0);

let task = ref(null as ITask | null);
let numeric = ref(null as INumeric | null);

emitter.on(EventNames.ConnectionLoopReconnected, fetchNumericAsync);

watch(
    () => route.query.numeric,
    (newNumericId, oldNumericId) => {
        scrollIntoNumeric(numericId.value);
        pullPageTitle(t('numeric-number', { number: oldNumericId }));
        pushPageTitle(t('numeric-number', { number: newNumericId }));
        fetchNumericAsync();
    },
);

watch(() => numeric.value?.taskId, fetchTaskAsync);

onMounted(() => {
    scrollIntoNumeric(numericId.value);
    emit('configure', { close });
    pushPageTitle(t('numeric-number', { number: numericId.value }));
    fetchNumericAsync();
});

onBeforeUnmount((): void => {
    emit('before-unmount');
    pullPageTitle(t('numeric-number', { number: numeric.value?.id }));
    emitter.off(EventNames.ConnectionLoopReconnected, fetchNumericAsync);
});

function close(): void {
    router.replace({
        query: {
            ...route.query,
            numeric: undefined,
        },
    });
}

function scrollIntoNumeric(numericId: number | string) {
    if (!numericId) {
        return;
    }

    const numericElement = document.querySelector(`.numeric-goal-selector[numeric-id='${numericId}']`) as HTMLElement;

    if (numericElement) {
        scrollIntoView(numericElement, {
            time: 250,
        });
    }
}

async function fetchTaskAsync(): Promise<void> {
    if (!numeric.value?.taskId || task.value?.id === numeric.value.taskId) {
        return;
    }

    try {
        task.value = await TaskService.findAsync(numeric.value?.taskId);
    } catch (error) {
        $error(error);
    }
}

async function fetchNumericAsync(): Promise<void> {
    emit('loading', true);

    try {
        const result = await NumericsService.queryAsync({
            whereId: [numericId.value],
            withArchived: true,
        });

        if (result.length === 0) {
            numeric.value = null;
            emit('no-data', true);
            return;
        }

        numeric.value = result[0];

        trackMutations(numeric.value);
    } catch (error) {
        $error(error);
    } finally {
        emit('loading', false);
    }
}
</script>
